import { getSelfMatchKeys, identityKeysIntersect } from "./anonymous-voter-identities";

export const VOTE_VISIBILITY = Object.freeze({
  FULL: "full_visibility",
  HIDDEN_WHILE_VOTING: "hidden_while_voting",
  HIDDEN_UNTIL_ALL_VOTED: "hidden_until_all_voted",
  HIDDEN_UNTIL_FINALIZED: "hidden_until_finalized",
  HIDDEN: "hidden",
});

export const DEFAULT_VOTE_VISIBILITY = VOTE_VISIBILITY.FULL;
export const DEFAULT_HIDE_VOTER_IDENTITIES = false;
export const VOTE_ANONYMIZATION = Object.freeze({
  NONE: "none",
  CREATOR_EXCLUDED: "creator_excluded",
  ALL_PARTICIPANTS: "all_participants",
});
export const DEFAULT_VOTE_ANONYMIZATION = VOTE_ANONYMIZATION.NONE;

export const VOTE_VISIBILITY_OPTIONS = Object.freeze([
  {
    value: VOTE_VISIBILITY.FULL,
    label: "Visible to participants immediately",
    description: "Participants see vote details as soon as voting starts.",
  },
  {
    value: VOTE_VISIBILITY.HIDDEN_WHILE_VOTING,
    label: "Visible after each participant votes",
    description: "Each participant unlocks vote details after submitting their own vote.",
  },
  {
    value: VOTE_VISIBILITY.HIDDEN_UNTIL_ALL_VOTED,
    label: "Visible after everyone votes",
    description: "Participants see vote details only once everyone has submitted.",
  },
  {
    value: VOTE_VISIBILITY.HIDDEN_UNTIL_FINALIZED,
    label: "Visible after finalization",
    description: "Participants see vote details only after the poll is finalized.",
  },
  {
    value: VOTE_VISIBILITY.HIDDEN,
    label: "Visible only to organizer",
    description: "Participants never see detailed vote selections.",
  },
]);

export const VOTE_ANONYMIZATION_OPTIONS = Object.freeze([
  {
    value: VOTE_ANONYMIZATION.NONE,
    label: "No anonymization",
    description: "Names are shown wherever vote details are visible.",
  },
  {
    value: VOTE_ANONYMIZATION.CREATOR_EXCLUDED,
    label: "Anonymous for participants",
    description: "Participants see aliases. Organizer still sees real names.",
  },
  {
    value: VOTE_ANONYMIZATION.ALL_PARTICIPANTS,
    label: "Anonymous for everyone",
    description: "Everyone sees aliases, including the organizer.",
  },
]);

const VOTE_VISIBILITY_SET = new Set(VOTE_VISIBILITY_OPTIONS.map((option) => option.value));
const VOTE_ANONYMIZATION_SET = new Set(
  VOTE_ANONYMIZATION_OPTIONS.map((option) => option.value)
);

export function resolveVoteVisibility(value) {
  return VOTE_VISIBILITY_SET.has(value) ? value : DEFAULT_VOTE_VISIBILITY;
}

export function resolveVoteAnonymization(value) {
  return VOTE_ANONYMIZATION_SET.has(value) ? value : DEFAULT_VOTE_ANONYMIZATION;
}

export function resolveHideVoterIdentities(value) {
  return value === true;
}

export function resolveHideVoterIdentitiesForVisibility(value, voteVisibility) {
  const normalizedVisibility = resolveVoteVisibility(voteVisibility);
  if (normalizedVisibility === VOTE_VISIBILITY.FULL) return false;
  return resolveHideVoterIdentities(value);
}

// Privacy-restrictiveness rank (higher = more private). Used to clamp an embedded
// poll's effective privacy to the MORE private of {parent, its own stored value}.
const VOTE_VISIBILITY_PRIVACY_RANK = Object.freeze({
  [VOTE_VISIBILITY.FULL]: 0,
  [VOTE_VISIBILITY.HIDDEN_WHILE_VOTING]: 1,
  [VOTE_VISIBILITY.HIDDEN_UNTIL_ALL_VOTED]: 2,
  [VOTE_VISIBILITY.HIDDEN_UNTIL_FINALIZED]: 3,
  [VOTE_VISIBILITY.HIDDEN]: 4,
});
const VOTE_ANONYMIZATION_PRIVACY_RANK = Object.freeze({
  [VOTE_ANONYMIZATION.NONE]: 0,
  [VOTE_ANONYMIZATION.CREATOR_EXCLUDED]: 1,
  [VOTE_ANONYMIZATION.ALL_PARTICIPANTS]: 2,
});

export function moreRestrictiveVoteVisibility(a, b) {
  const resolvedA = resolveVoteVisibility(a);
  const resolvedB = resolveVoteVisibility(b);
  return VOTE_VISIBILITY_PRIVACY_RANK[resolvedA] >= VOTE_VISIBILITY_PRIVACY_RANK[resolvedB]
    ? resolvedA
    : resolvedB;
}

export function moreRestrictiveVoteAnonymization(a, b) {
  const resolvedA = resolveVoteAnonymization(a);
  const resolvedB = resolveVoteAnonymization(b);
  return VOTE_ANONYMIZATION_PRIVACY_RANK[resolvedA] >= VOTE_ANONYMIZATION_PRIVACY_RANK[resolvedB]
    ? resolvedA
    : resolvedB;
}

// Effective vote privacy for an embedded ("add-on") poll: the parent scheduler's
// privacy, clamped so it is never LESS private than what the poll itself stored.
// Embedded polls inherit the parent's settings (per-poll controls were removed),
// while this clamp guarantees inheritance can only TIGHTEN — a poll whose voters
// were promised "anonymous for everyone" (or a hidden visibility) can never be
// retroactively de-anonymized / revealed by a less-private parent. New polls
// store the defaults (FULL / none), so their effective privacy is exactly the
// parent's; older polls with stronger stored privacy keep it as a floor. When the
// parent hasn't loaded yet its fields are undefined → treated as the FULL/none
// default, so the result collapses to the POLL'S OWN stored value: an old poll's
// stronger stored privacy still protects it, but a NEW poll (stored FULL/none)
// briefly resolves to FULL/none until the parent loads. Callers therefore must
// not RENDER embedded-poll identities before the parent scheduler has loaded
// (SchedulerPage + dashboard gate the cards behind their scheduler load); the
// only pre-load residual is the votes-subscription hook briefly holding vote docs
// it won't display — an accepted residual under the guardrails-not-fortress model.
export function resolveEmbeddedPollPrivacy({ parent, poll } = {}) {
  const voteVisibility = moreRestrictiveVoteVisibility(
    parent?.voteVisibility,
    poll?.voteVisibility
  );
  const voteAnonymization = moreRestrictiveVoteAnonymization(
    parent?.voteAnonymization,
    poll?.voteAnonymization
  );
  const hideVoterIdentities = resolveHideVoterIdentitiesForVisibility(
    resolveHideVoterIdentities(parent?.hideVoterIdentities) ||
      resolveHideVoterIdentities(poll?.hideVoterIdentities),
    voteVisibility
  );
  return { voteVisibility, voteAnonymization, hideVoterIdentities };
}

export function canViewVoterIdentities({ isCreator = false, hideVoterIdentities = false } = {}) {
  if (isCreator) return true;
  return resolveHideVoterIdentities(hideVoterIdentities) !== true;
}

export function getVoteIdentityDisplayMode({
  isCreator = false,
  hideVoterIdentities = false,
  voteAnonymization,
  detailsRevealed,
} = {}) {
  // detailsRevealed is REQUIRED: fail loud in dev/test, fail closed in prod.
  // Self is intentionally NOT a parameter here — self-exemption is handled at
  // the render layer (see anonymous-voter-placeholders / anonymizeUsers).
  if (detailsRevealed === undefined || detailsRevealed === null) {
    if (import.meta.env.DEV) {
      throw new Error("getVoteIdentityDisplayMode: detailsRevealed is required");
    }
    return "hidden";
  }

  const anon = resolveVoteAnonymization(voteAnonymization);
  const anonApplies =
    anon === VOTE_ANONYMIZATION.ALL_PARTICIPANTS ||
    (anon === VOTE_ANONYMIZATION.CREATOR_EXCLUDED && !isCreator);

  // R14: creator is never hidden; aliased only when anonymization applies.
  if (isCreator) return anonApplies ? "anonymous" : "named";

  // R9 + R12: hide identity until reveal when the hide flag OR anonymization is on.
  const hiddenUntilReveal =
    resolveHideVoterIdentities(hideVoterIdentities) || anonApplies;
  if (hiddenUntilReveal && !detailsRevealed) return "hidden";

  return anonApplies ? "anonymous" : "named";
}

/**
 * Effective "hide voter identities until reveal" flag. Anonymization wins: when
 * identities are aliased, hide-until-reveal is never used, so a stale stored
 * hide=true under anonymization != none is treated as false.
 * @param {*} voteAnonymization - raw stored anonymization value.
 * @param {*} hideVoterIdentities - raw stored hide flag.
 * @returns {boolean}
 */
export function effectiveHideVoterIdentities(voteAnonymization, hideVoterIdentities) {
  if (resolveVoteAnonymization(voteAnonymization) !== VOTE_ANONYMIZATION.NONE) return false;
  return resolveHideVoterIdentities(hideVoterIdentities) === true;
}

/**
 * Roster (invitee-list) identity mode for a participant. Gated by anonymization
 * ONLY — the roster is never "hidden"/silhouetted, only named or anonymous. Self
 * is always named.
 * @param {object} opts
 * @param {boolean} [opts.isCreator=false]
 * @param {boolean} [opts.isSelf=false]
 * @param {*} opts.voteAnonymization
 * @returns {"named"|"anonymous"}
 */
export function getRosterIdentityMode({ isCreator = false, isSelf = false, voteAnonymization } = {}) {
  if (isSelf) return "named";
  const anon = resolveVoteAnonymization(voteAnonymization);
  const anonApplies =
    anon === VOTE_ANONYMIZATION.ALL_PARTICIPANTS ||
    (anon === VOTE_ANONYMIZATION.CREATOR_EXCLUDED && !isCreator);
  return anonApplies ? "anonymous" : "named";
}

/**
 * Per-target RENDER contract for a participant vote bubble. Pure predicate: it
 * decides how a single participant row/bubble is rendered for the current
 * viewer. Self-exemption uses STRONG identity keys only (id/uid/email/public),
 * never displayName.
 *
 * @param {object} opts
 * @param {object|null} opts.currentUser - the logged-in viewer's identity.
 * @param {object} opts.viewer - `{ isCreator, hasVoted }` for the viewer.
 * @param {object} opts.target - `{ user, hasVoted, statusKnown }`; `statusKnown`
 *   is the caller's signal that the target's vote doc is loaded AND permitted.
 * @param {object} opts.settings - `{ voteVisibility, voteAnonymization, hideVoterIdentities }`.
 * @param {object} opts.pollState - `{ allParticipantsVoted, isFinalized }`.
 * @returns {{rosterName, bubbleMode, showStatus, statusKnown, statusValue, showDetails, canOpenIdentityActions}}
 */
export function getParticipantVoteView({
  currentUser,
  viewer,
  target,
  settings,
  pollState,
} = {}) {
  const anon = resolveVoteAnonymization(settings?.voteAnonymization);
  const isSelf = !!(
    currentUser &&
    target?.user &&
    identityKeysIntersect(getSelfMatchKeys(currentUser), getSelfMatchKeys(target.user))
  );
  const isCreator = !!viewer?.isCreator;
  const anonApplies =
    anon === VOTE_ANONYMIZATION.ALL_PARTICIPANTS ||
    (anon === VOTE_ANONYMIZATION.CREATOR_EXCLUDED && !isCreator);
  const detailsRevealed = canViewOtherVotesForUser({
    voteVisibility: settings?.voteVisibility,
    isCreator,
    hasVoted: !!viewer?.hasVoted,
    allParticipantsVoted: !!pollState?.allParticipantsVoted,
    isFinalized: !!pollState?.isFinalized,
  });
  const effHide = effectiveHideVoterIdentities(
    settings?.voteAnonymization,
    settings?.hideVoterIdentities
  );

  // Bubble identity (status-bearing).
  let bubbleMode;
  if (isSelf) bubbleMode = "named";
  else if (isCreator) bubbleMode = anonApplies ? "anonymous" : "named";
  else if (anonApplies) bubbleMode = "anonymous";
  else if (effHide && !detailsRevealed) bubbleMode = "hidden";
  else bubbleMode = "named";

  const rosterName = getRosterIdentityMode({
    isCreator,
    isSelf,
    voteAnonymization: settings?.voteAnonymization,
  });
  const showStatus = bubbleMode !== "hidden";
  const statusKnown = showStatus ? (isSelf ? true : !!target?.statusKnown) : false;
  const statusValue =
    showStatus && statusKnown ? (target?.hasVoted ? "voted" : "pending") : "unknown";
  const showDetails = detailsRevealed;
  // Only "named" bubbles get profile/friend actions. Aliases ("anonymous") and
  // silhouettes ("hidden") are already excluded by that check alone — no need
  // to also exclude self, which is always "named" and may open its own profile.
  const canOpenIdentityActions = bubbleMode === "named";

  return {
    rosterName,
    bubbleMode,
    showStatus,
    statusKnown,
    statusValue,
    showDetails,
    canOpenIdentityActions,
  };
}

export function canViewOtherVotesForUser({
  voteVisibility,
  isCreator = false,
  hasVoted = false,
  allParticipantsVoted = false,
  isFinalized = false,
} = {}) {
  if (isCreator) return true;

  const mode = resolveVoteVisibility(voteVisibility);
  if (mode === VOTE_VISIBILITY.FULL) return true;
  if (mode === VOTE_VISIBILITY.HIDDEN_WHILE_VOTING) return hasVoted;
  if (mode === VOTE_VISIBILITY.HIDDEN_UNTIL_ALL_VOTED) return allParticipantsVoted;
  if (mode === VOTE_VISIBILITY.HIDDEN_UNTIL_FINALIZED) return isFinalized;
  return false;
}

export function canViewOtherVotesPublicly({
  voteVisibility,
  allParticipantsVoted = false,
  isFinalized = false,
} = {}) {
  const mode = resolveVoteVisibility(voteVisibility);
  if (mode === VOTE_VISIBILITY.FULL) return true;
  if (mode === VOTE_VISIBILITY.HIDDEN_UNTIL_ALL_VOTED) return allParticipantsVoted;
  if (mode === VOTE_VISIBILITY.HIDDEN_UNTIL_FINALIZED) return isFinalized;
  return false;
}
